import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { ticketService } from '../services/ticketService'

/**
 * Tickets Store
 * Manages all ticket-related state and API interactions
 */
export const useTicketsStore = create(
  persist(
    (set, get) => ({
      // State
      tickets: [],
      selectedTicket: null,
      loading: false,
      error: null,
      filters: {
        status: null,
        priority: null,
        assignedTo: null,
        search: '',
      },

      // ==================== FETCH OPERATIONS ====================

      /**
       * Fetch all tickets from API
       */
      fetchTickets: async (filterOptions = {}) => {
        set({ loading: true, error: null })
        try {
          const data = await ticketService.getTickets(filterOptions)
          set({ tickets: data.tickets || [], error: null })
          return data
        } catch (error) {
          set({ error: error.message })
          console.error('Failed to fetch tickets:', error)
          throw error
        } finally {
          set({ loading: false })
        }
      },

      /**
       * Fetch single ticket by ID
       */
      fetchTicket: async (id) => {
        set({ loading: true, error: null })
        try {
          const data = await ticketService.getTicketById(id)
          set({ selectedTicket: data, error: null })
          return data
        } catch (error) {
          set({ error: error.message })
          console.error('Failed to fetch ticket:', error)
          throw error
        } finally {
          set({ loading: false })
        }
      },

      // ==================== CREATE OPERATION ====================

      /**
       * Create new ticket
       */
      createTicket: async (ticketData) => {
        set({ loading: true, error: null })
        try {
          const newTicket = await ticketService.createTicket(ticketData)
          set((state) => ({
            tickets: [newTicket, ...state.tickets],
            error: null,
          }))
          return newTicket
        } catch (error) {
          set({ error: error.message })
          console.error('Failed to create ticket:', error)
          throw error
        } finally {
          set({ loading: false })
        }
      },

      // ==================== UPDATE OPERATIONS ====================

      /**
       * Update existing ticket
       */
      updateTicket: async (id, updates) => {
        set({ loading: true, error: null })
        try {
          const updatedTicket = await ticketService.updateTicket(id, updates)
          set((state) => ({
            tickets: state.tickets.map((t) =>
              t.id === id ? { ...t, ...updatedTicket } : t
            ),
            selectedTicket: state.selectedTicket?.id === id ? updatedTicket : state.selectedTicket,
            error: null,
          }))
          return updatedTicket
        } catch (error) {
          set({ error: error.message })
          console.error('Failed to update ticket:', error)
          throw error
        } finally {
          set({ loading: false })
        }
      },

      /**
       * Update ticket status
       */
      updateStatus: async (id, status) => {
        return get().updateTicket(id, { status })
      },

      /**
       * Update ticket priority
       */
      updatePriority: async (id, priority) => {
        return get().updateTicket(id, { priority })
      },

      /**
       * Assign ticket to agent
       */
      assignTicket: async (id, agentId) => {
        set({ loading: true, error: null })
        try {
          const updatedTicket = await ticketService.assignTicket(id, agentId)
          set((state) => ({
            tickets: state.tickets.map((t) =>
              t.id === id ? updatedTicket : t
            ),
            selectedTicket: state.selectedTicket?.id === id ? updatedTicket : state.selectedTicket,
            error: null,
          }))
          return updatedTicket
        } catch (error) {
          set({ error: error.message })
          console.error('Failed to assign ticket:', error)
          throw error
        } finally {
          set({ loading: false })
        }
      },

      /**
       * Escalate ticket
       */
      escalateTicket: async (id, reason) => {
        set({ loading: true, error: null })
        try {
          const updatedTicket = await ticketService.escalateTicket(id, reason)
          set((state) => ({
            tickets: state.tickets.map((t) =>
              t.id === id ? updatedTicket : t
            ),
            selectedTicket: state.selectedTicket?.id === id ? updatedTicket : state.selectedTicket,
            error: null,
          }))
          return updatedTicket
        } catch (error) {
          set({ error: error.message })
          console.error('Failed to escalate ticket:', error)
          throw error
        } finally {
          set({ loading: false })
        }
      },

      // ==================== MESSAGE OPERATIONS ====================

      /**
       * Add message/comment to ticket
       */
      addMessage: async (ticketId, messageText) => {
        set({ loading: true, error: null })
        try {
          const newMessage = await ticketService.addMessage(ticketId, messageText)
          set((state) => ({
            selectedTicket: state.selectedTicket?.id === ticketId
              ? {
                  ...state.selectedTicket,
                  messages: [...(state.selectedTicket.messages || []), newMessage],
                }
              : state.selectedTicket,
            error: null,
          }))
          return newMessage
        } catch (error) {
          set({ error: error.message })
          console.error('Failed to add message:', error)
          throw error
        } finally {
          set({ loading: false })
        }
      },

      /**
       * Get all messages for ticket
       */
      fetchMessages: async (ticketId) => {
        set({ loading: true, error: null })
        try {
          const messages = await ticketService.getMessages(ticketId)
          set((state) => ({
            selectedTicket: state.selectedTicket?.id === ticketId
              ? { ...state.selectedTicket, messages }
              : state.selectedTicket,
            error: null,
          }))
          return messages
        } catch (error) {
          set({ error: error.message })
          console.error('Failed to fetch messages:', error)
          throw error
        } finally {
          set({ loading: false })
        }
      },

      // ==================== DELETE OPERATION ====================

      /**
       * Delete ticket
       */
      deleteTicket: async (id) => {
        set({ loading: true, error: null })
        try {
          await ticketService.deleteTicket(id)
          set((state) => ({
            tickets: state.tickets.filter((t) => t.id !== id),
            selectedTicket: state.selectedTicket?.id === id ? null : state.selectedTicket,
            error: null,
          }))
        } catch (error) {
          set({ error: error.message })
          console.error('Failed to delete ticket:', error)
          throw error
        } finally {
          set({ loading: false })
        }
      },

      // ==================== FILTER & SEARCH ====================

      /**
       * Update filters
       */
      setFilters: (newFilters) => {
        set((state) => ({
          filters: { ...state.filters, ...newFilters },
        }))
      },

      /**
       * Get filtered tickets
       */
      getFilteredTickets: () => {
        const state = get()
        let filtered = state.tickets

        if (state.filters.status) {
          filtered = filtered.filter((t) => t.status === state.filters.status)
        }
        if (state.filters.priority) {
          filtered = filtered.filter((t) => t.priority === state.filters.priority)
        }
        if (state.filters.assignedTo) {
          filtered = filtered.filter((t) => t.assigned_to === state.filters.assignedTo)
        }
        if (state.filters.search) {
          const search = state.filters.search.toLowerCase()
          filtered = filtered.filter(
            (t) =>
              t.subject?.toLowerCase().includes(search) ||
              t.description?.toLowerCase().includes(search) ||
              t.customer_email?.toLowerCase().includes(search)
          )
        }

        return filtered
      },

      // ==================== LOCAL STATE ====================

      /**
       * Set selected ticket
       */
      setSelectedTicket: (ticket) => {
        set({ selectedTicket: ticket })
      },

      /**
       * Clear selected ticket
       */
      clearSelectedTicket: () => {
        set({ selectedTicket: null })
      },

      /**
       * Clear error
       */
      clearError: () => {
        set({ error: null })
      },
    }),
    {
      name: 'tickets-store',
      getStorage: () => localStorage,
    }
  )
)
